import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

import type {
  BillingCarePlan,
  BillingRow,
  BillingTab,
  BillingTaskType,
} from "../../hexagon/core/ports/BillingModule";

/**
 * Billing Module slice.
 *
 * Replaces the legacy `state.billing` slice + per-user localStorage entries
 * (`billingNewMonths`, `billingNewSearchText`, `billingNewActiveTab`,
 * `billingNewTaskType`). Redux-persist owns the filter state under the
 * `billing` key — fetched rows are kept in-memory only.
 *
 * NOTE: rows here are the RAW API rows (typed as `BillingRow`). Client-derived
 * fields (`buttonType`, `rowState`, `monthKey`, `monthLabel`, …) are NOT stored
 * here — they are computed lazily from selectors so they always reflect the
 * latest permissions/billing_type. This avoids the legacy footgun of mutating
 * the row in place at fetch time and then having stale derivations after
 * permissions change.
 */

export interface IBillingStateSlice {
  /** Months currently selected in the picker. Max 2 entries (UI enforced). */
  selectedMonths: string[]; // 'YYYY-MM'
  /** Free-text search applied across all rows. */
  searchText: string;
  /** Active tab. '0' = TODO, '1' = Ready to Bill, '2' = Billed (mirrors AntD Tabs keys). */
  activeTab: BillingTab;
  /** TODO-tab task-type filter. */
  todoTaskType: BillingTaskType;
  /**
   * Raw rows keyed by month ('YYYY-MM'). Replaces the legacy `billingList2`
   * (a flat list with derived `monthKey`) — the keyed shape removes the need
   * for the in-place `monthKey` mutation.
   */
  rowsByMonth: Record<string, BillingRow[]>;
  /** True while at least one month's fetch is in flight. */
  isLoading: boolean;
  /** Last error message from the service (null when no error). */
  error: string | null;
}

const name = "billing";

/**
 * Build a 'YYYY-MM' key from a Date.
 * Uses UTC parts so the persisted key is stable across timezones.
 */
const monthKey = (d: Date): string =>
  `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;

/**
 * Default month selection — **two months** (previous + current), matching
 * legacy `billingNew/index.js` initial state. Surfaces the most recent
 * billable period plus the current month so MDs land on a useful view.
 */
const defaultSelectedMonths = (): string[] => {
  const now = new Date();
  const prev = new Date(
    Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)
  );
  return [monthKey(prev), monthKey(now)];
};

export const initialState: IBillingStateSlice = {
  selectedMonths: defaultSelectedMonths(),
  searchText: "",
  activeTab: "todo",
  todoTaskType: "",
  rowsByMonth: {},
  isLoading: false,
  error: null,
};

export const billingSlice = createSlice({
  name,
  initialState,
  reducers: {
    setSelectedMonths(state, action: PayloadAction<string[]>) {
      // UI also caps at 2 — defensive cap here so reducer is self-consistent.
      state.selectedMonths = action.payload.slice(0, 2);
    },
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setActiveTab(state, action: PayloadAction<BillingTab>) {
      state.activeTab = action.payload;
    },
    setTodoTaskType(state, action: PayloadAction<BillingTaskType>) {
      state.todoTaskType = action.payload;
    },
    setRowsForMonth(
      state,
      action: PayloadAction<{ month: string; rows: BillingRow[] }>
    ) {
      state.rowsByMonth[action.payload.month] = action.payload.rows;
    },
    /** Replace one row in the in-memory cache (e.g. after sign-off mutation). */
    upsertRow(
      state,
      action: PayloadAction<{ month: string; row: BillingRow }>
    ) {
      const { month, row } = action.payload;
      const existing = state.rowsByMonth[month];
      if (!existing) {
        state.rowsByMonth[month] = [row];
        return;
      }
      const idx = existing.findIndex((r) => r.id === row.id);
      if (idx === -1) {
        existing.push(row);
      } else {
        existing[idx] = row;
      }
    },
    /**
     * Optimistically reflect a newly-created Order Note on the cached row so
     * the listing flips from "Add Order Note" (awaitingCarePlan) to
     * "Chart Review" (awaitingSignature) without a refetch. Mirrors the data
     * the API would return: a populated `care_plan.ClinicPatientCarePlan` and
     * the updated diagnosis JSON. No-op if the month/patient isn't cached.
     */
    applyOrderNoteCreated(
      state,
      action: PayloadAction<{
        month: string;
        patientId: number | string;
        carePlan: BillingCarePlan;
        diagnosisJson?: string;
      }>
    ) {
      const { month, patientId, carePlan, diagnosisJson } = action.payload;
      const rows = state.rowsByMonth[month];
      if (!rows) return;
      const row = rows.find((r) => Number(r.id) === Number(patientId));
      if (!row) return;
      row.care_plan = carePlan;
      if (diagnosisJson != null) row.diagnosis = diagnosisJson;
    },
    clearRows(state) {
      state.rowsByMonth = {};
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      if (action.payload !== null) state.isLoading = false;
    },
    resetBillingFilters(state) {
      state.searchText = "";
      state.activeTab = "todo";
      state.todoTaskType = "";
      state.selectedMonths = defaultSelectedMonths();
    },
  },
});

export const {
  setSelectedMonths,
  setSearchText,
  setActiveTab,
  setTodoTaskType,
  setRowsForMonth,
  upsertRow,
  applyOrderNoteCreated,
  clearRows,
  setLoading,
  setError,
  resetBillingFilters,
} = billingSlice.actions;

// ─── Persistence ──────────────────────────────────────────────────────────────
//
// Only persist user-controlled filters; in-memory row caches are deliberately
// excluded so a refresh always pulls fresh server data.
const persistedReducer = persistReducer(
  {
    key: name,
    storage,
    whitelist: ["selectedMonths", "searchText", "activeTab", "todoTaskType"],
  },
  billingSlice.reducer
);

export default persistedReducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

export const selectBillingFilters = (state: { billing: IBillingStateSlice }) =>
  ({
    selectedMonths: state.billing.selectedMonths,
    searchText: state.billing.searchText,
    activeTab: state.billing.activeTab,
    todoTaskType: state.billing.todoTaskType,
  } as const);

export const selectBillingRows = (state: { billing: IBillingStateSlice }) =>
  state.billing.rowsByMonth;

export const selectBillingIsLoading = (state: {
  billing: IBillingStateSlice;
}) => state.billing.isLoading;

export const selectBillingError = (state: { billing: IBillingStateSlice }) =>
  state.billing.error;
